import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval, Observable, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environments';

export interface NotificationData {
  id?: number;
  title: string;
  message: string;
  notification_type: 'info' | 'warning' | 'success' | 'error';
  icon?: string;
  icon_color?: string;
  created_by: number;
  expires_at?: string;
  is_active?: boolean;
  created_at?: string ;
  created_by_name?: string;
  total_users_notified?: number;
  users_read_count?: number;
  is_read: boolean;
  read_at?: string;
}

export interface GroupedNotifications {
  today: NotificationData[];
  yesterday: NotificationData[];
  this_week: NotificationData[];
  older: NotificationData[];
}

export interface UserNotificationsResponse {
  notifications: GroupedNotifications;
  unread_count: number;
  total_count: number;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
    private BASE_URL = environment.apiUrl + 'api/notifications';
    constructor(private http: HttpClient) { }
    unreadCount = signal<number>(0);
    notifications = signal<GroupedNotifications>({
        today: [],
        yesterday: [],
        this_week: [],
        older: []
    });

    private startNotificationPolling() {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
            if (user.user_id) {
            interval(30000).pipe(
                switchMap(() => this.getUnreadCount(user.user_id))
            ).subscribe({
                next: (response) => this.unreadCount.set(response.unread_count),
                error: (error) => console.error('Erreur polling notifications:', error)
            });
            }
    }
    getUserNotifications(userId: number): Observable<UserNotificationsResponse> {
        return this.http.get<UserNotificationsResponse>(`${this.BASE_URL}/user/${userId}`).pipe(
        tap(response => {
            this.notifications.set(response.notifications);
            this.unreadCount.set(response.unread_count);
        })
        );
    }

    markNotificationRead(userId: number, notificationId: number): Observable<any> {
        return this.http.post(`${this.BASE_URL}/user/${userId}/mark-read/${notificationId}`, {}).pipe(
        tap(() => {
            const current = this.notifications();
            Object.keys(current).forEach(key => {
            const group = current[key as keyof GroupedNotifications];
            const notification = group.find(n => n.id === notificationId);
            if (notification && !notification.is_read) {
                notification.is_read = true;
                notification.read_at = new Date().toISOString();
                this.unreadCount.update(count => count - 1);
            }
            });
            this.notifications.set({...current});
        })
        );
    }

    markAllNotificationsRead(userId: number): Observable<any> {
        return this.http.post(`${this.BASE_URL}/user/${userId}/mark-all-read`, {}).pipe(
        tap(() => {
            const current = this.notifications();
            Object.keys(current).forEach(key => {
            const group = current[key as keyof GroupedNotifications];
            group.forEach(notification => {
                if (!notification.is_read) {
                notification.is_read = true;
                notification.read_at = new Date().toISOString();
                }
            });
            });
            this.notifications.set({...current});
            this.unreadCount.set(0);
            })
        );
    }

    getUnreadCount(userId: number): Observable<{unread_count: number}> {
        return this.http.get<{unread_count: number}>(`${this.BASE_URL}/user/${userId}/unread-count`);
    }

    createNotification(notificationData: NotificationData): Observable<any> {
        return this.http.post(`${this.BASE_URL}/admin/create`, notificationData);
    }

    getAllNotificationsAdmin(): Observable<NotificationData[]> {
        return this.http.get<NotificationData[]>(`${this.BASE_URL}/admin/all`);
    }

    deactivateNotification(notificationId: number): Observable<any> {
        return this.http.put(`${this.BASE_URL}/admin/${notificationId}/deactivate`, {});
    }

}